// @ts-nocheck
import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Autocomplete, TextField } from '@mui/material';

import { ReactComponent as DropdownArrow } from '../../../../assets/images/DropdownArrow.svg';
import { useEmailTemplateListQuery } from '../../../../features/configuration/emailTemplate/api/emailTemplateQueries';
import { getErrorHelperText } from '../../../../utils/Utils';
import { EMPTY_ARRAY } from '../../../../const/CommonConst';
import { DRAFT_INPUT_SX } from './TextFieldComponent';
import { scheduleDraftPersistOnBlur } from '../../../../hooks/useDraftAutoSave.jsx';

const getEmailTemplateListOptionLabel = (option) =>
  `${option?.email_template_id} (${option?.event_name})`;
const isOptionEqualToValue = (option, value) =>
  String(option?.email_template_id) === String(value?.email_template_id);

const EmailTemplateListFieldComponent = ({
  label,
  titleLabel,
  value,
  onChange,
  attribute,
  entityDetailsFormikForm,
  required = false,
  hasDraftChip = false,
  onDraftBlur,
}) => {
  const { data: emailTemplateList = EMPTY_ARRAY } = useEmailTemplateListQuery();

  const selectedOption = useMemo(() => {
    return (
      emailTemplateList.find(
        (option) =>
          String(option?.email_template_id) === String(value?.email_template_id)
      ) || null
    );
  }, [emailTemplateList, value]);

  const handleChange = useCallback(
    (_event, newValue) => {
      if (newValue) {
        onChange({
          email_template_id: newValue?.email_template_id,
          event_type: newValue?.event_type,
          event_name: newValue?.event_name,
        });
      } else {
        onChange(null);
        scheduleDraftPersistOnBlur(onDraftBlur);
      }
    },
    [onChange, onDraftBlur]
  );

  const handleClose = useCallback(() => {
    onDraftBlur?.();
  }, [onDraftBlur]);

  const renderInput = useCallback(
    (params) => (
      <TextField
        {...params}
        label={label}
        title={titleLabel}
        required={required}
        placeholder="Select email template"
        error={Boolean(
          getErrorHelperText(
            entityDetailsFormikForm?.errors?.[attribute?.id],
            entityDetailsFormikForm?.touched?.[attribute?.id]
          )
        )}
        helperText={getErrorHelperText(
          entityDetailsFormikForm?.errors?.[attribute?.id],
          entityDetailsFormikForm?.touched?.[attribute?.id]
        )}
        sx={hasDraftChip ? DRAFT_INPUT_SX : undefined}
        slotProps={{
          ...params?.slotProps,
          input: {
            ...params?.slotProps?.input,
            placeholder: 'Select email template',
          },

          inputLabel: {
            ...params?.slotProps?.inputLabel,
            shrink: true,
          },
        }}
      />
    ),
    [
      attribute?.id,
      entityDetailsFormikForm,
      label,
      required,
      titleLabel,
      hasDraftChip,
    ]
  );

  return (
    <Autocomplete
      value={selectedOption}
      options={emailTemplateList}
      onChange={handleChange}
      onClose={handleClose}
      noOptionsText="No email templates present."
      popupIcon={<DropdownArrow />}
      getOptionLabel={getEmailTemplateListOptionLabel}
      isOptionEqualToValue={isOptionEqualToValue}
      renderInput={renderInput}
    />
  );
};

EmailTemplateListFieldComponent.propTypes = {
  label: PropTypes.string.isRequired,
  titleLabel: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.shape({}),
  ]),
  onChange: PropTypes.func.isRequired,
  required: PropTypes.bool,
  attribute: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
  entityDetailsFormikForm: PropTypes.shape({
    setFieldTouched: PropTypes.func,
    errors: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.string])),
    touched: PropTypes.objectOf(PropTypes.bool),
  }),
  hasDraftChip: PropTypes.bool,
  onDraftBlur: PropTypes.func,
};

export default EmailTemplateListFieldComponent;
